import { AcceptPage, PolicyRaw } from '@/cms/definitions/content-types';
import store from '@/store/store';
import { CmsField, CmsPolicy, DigitalAcceptCmsPage, RadioButtonField } from '@/types/types';
import { cloneDeep } from 'lodash';
import { getBrand } from './environmentService';
import { formatDate, formatter } from './FormatService';
import { getCancellationDate } from './FieldService';
import { POLICY_FIELDS } from '@/types/enums';

export const setReplacements = (
	cmsModel: AcceptPage,
	proposal: DigitalAcceptCmsPage,
	replacements: Map<string, string>
) => {
	cmsModel.replacements?.forEach((replacement: { key: string; value: string }) => {
		replacements.set(`{{${replacement.key}}}`, replacement.value);
	});
	// make certain brand is set
	if (!replacements.get('{{SYSTEM_BRAND}}')) {
		replacements.set('{{SYSTEM_BRAND}}', getBrand());
	}
	replacements.set('{{SYSTEM_OFFER_ID}}', proposal.quote_id);
	replacements.set('{{SYSTEM_TOTAL_PRICE}}', `${formatter.format(proposal.price)}`);
	if (proposal.fees) {
		replacements.set('{{SYSTEM_TOTAL_FEES}}', `${formatter.format(proposal.fees)}`);
	}

	const currentProductTypesArray: string[] = [];
	const currentProductTitlesArray: string[] = [];
	let affinity: string;
	proposal.policies.forEach((policy) => {
		if (!currentProductTypesArray.includes(policy.type)) {
			currentProductTypesArray.push(policy.type);
			if (cmsModel.contentType === 'AcceptPage') {
				const title = getCmsPolicy(cmsModel, policy.type)?.content?.title;
				title ? currentProductTitlesArray.push(title) : currentProductTitlesArray.push(policy.type);
			} else {
				cmsModel.productNames.forEach((prod) => {
					if (prod.key.toLowerCase() === policy.type.toLowerCase()) {
						prod.value
							? currentProductTitlesArray.push(prod.value)
							: currentProductTitlesArray.push(policy.type);
					}
				});
			}
		}
		if (policy.affinity) {
			// for now, last affinity will be the one set
			affinity = policy.affinity;
		}
	});
	if (affinity) {
		replacements.set('{{SYSTEM_AFFINITY}}', affinity);
		replacements.set('{{SYSTEM_AFFINITY_AMOUNT}}', 'Rabat inkluderet');
	}
	replacements.set(
		'{{SYSTEM_CURRENT_PRODUCTS}}',
		currentProductTitlesArray.join(', ').replace(/,(?=[^,]*$)/, ' og') // replace last , with ' og'
	);
};

export const handleTermination = (stepId: string, inputFields: CmsField[], policy) => {
	const coverStart = getCancellationDate(stepId, inputFields);
	if (coverStart) {
		const cancellationOptions = inputFields.find(
			(field) => field.id === POLICY_FIELDS.termination_option
		) as RadioButtonField;

		if (cancellationOptions) {
			cancellationOptions.labels[0] += ` - ${formatDate(coverStart)}`;
		}
	}

	const cancelExistingPolicy = inputFields.find((field) => field.id === POLICY_FIELDS.cancelExistingPolicy);

	if (!cancelExistingPolicy) {
		return;
	}

	if (!cancelExistingPolicy?.value || cancelExistingPolicy?.value === 'false') {
		cancelExistingPolicy.value = 'false';
		cancelExistingPolicy.hidden = true;
		cancelExistingPolicy.isValid = true;
	}
	// set termination to true if there is a company
	if (policy?.termination?.[POLICY_FIELDS.previous_company_id]) {
		if (cancelExistingPolicy) {
			cancelExistingPolicy.value = 'true';
			cancelExistingPolicy.readonly = true;
			cancelExistingPolicy.isValid = true;
			cancelExistingPolicy.hidden = false;
		}
	}
	if (cancelExistingPolicy.value === 'true') {
		const cancelExistingPolicyText = inputFields.find(
			(field) => field.id === POLICY_FIELDS.cancelExistingPolicyText
		);
		if (cancelExistingPolicyText) {
			cancelExistingPolicyText.readonly = true;
		}
	}
};

export const handlePayment = (inputFields: CmsField[]) => {
	const paymentMethod = inputFields?.find((field) => field.id === POLICY_FIELDS.payment_method);
	if (paymentMethod?.value === 'PBS') {
		paymentMethod.readonly = true;
		// hide regNo and account
		const regnNoAccount = inputFields?.filter(
			(field) => field.id === POLICY_FIELDS.account || field.id === POLICY_FIELDS.reg_no
		);
		regnNoAccount?.forEach((field) => {
			field.hidden = true;
			field.isValid = true;
			field.dependentId = undefined;
			field.dependentValue = undefined;
		});
	}
};

export const handleDescription = (policy) => {
	// TODO create new mapping service for products
	// job_title -> accident
	// brand -> trailer
	// coverage_area -> travel 'EUROPE' | 'WORLD*

	let specificDescription = policy.policy_object?.brand;

	if (!specificDescription && policy.type === 'ACCIDENT') {
		specificDescription = policy.policy_object?.name;
	}

	if (policy?.type === 'TRAVEL') {
		const coverArea = policy.policy_object?.coverage_area;
		if (coverArea === 'EUROPE') {
			specificDescription = 'Dækker Europa';
		} else if (coverArea === 'WORLD') {
			specificDescription = 'Dækker Verden';
		}
	}

	if (specificDescription) {
		policy.description = specificDescription;
	}
};

export const handleTitle = (policy, cmsPolicy: CmsPolicy) => {
	// TODO create new mapping service for products
	// cmsPolicy is defined on acceptPage not receiptPage
	if (cmsPolicy) {
		policy.title = cmsPolicy.title;
	}

	if (policy.type === 'ACCIDENT') {
		switch (policy.policy_object?.accident_insurance_type) {
			case 'ADULT':
				policy.title = 'Voksenulykke'; //'Ulykkesforsikring Voksen';
				break;
			case 'CHILD':
				policy.title = 'Børneulykke';
				break;
			case 'EXTENDED_REPLACEMENT':
				policy.title = 'Udvidet strakserstatning';
				break;
			case 'ASSAULT':
				policy.title = 'Overfald';
				break;
			default:
				break;
		}

		if (cmsPolicy && policy.title) {
			cmsPolicy.title = policy.title;
		}
	}
};
export const getCmsPolicy = (cmsModel: AcceptPage, policyType: string): PolicyRaw => {
	let pType = policyType;
	if (policyType.includes('CONTENT')) {
		pType = 'CONTENT';
	}
	if (policyType.includes('MOPED')) {
		pType = 'MOPED';
	}

	return cmsModel.adjustPoliciesBlock?.policyBlock?.items?.find((policyBlock) => {
		if (policyBlock.content.type.toLocaleLowerCase() === pType.toLocaleLowerCase()) {
			policyBlock.content.originalType = policyType;
			return policyBlock;
		}
	});
};

export const getModifiedCmsPolicy = (cmsModel: AcceptPage, policyType: string): CmsPolicy => {
	const cmsPolicy = getCmsPolicy(cmsModel, policyType);
	// always work on copy of content structure
	const policy = cloneDeep(cmsPolicy.content);
	simplifyInputFields(policy);
	return policy as any;
};

// remove api layers
export const simplifyInputFields = (step) => {
	const inputFields = [];
	// when develop input fields are already mapped when auto compiling
	if (step.inputFields?.items) {
		step.inputFields?.items?.forEach((field) => {
			inputFields.push(field.content);
		});
		step.inputFields = inputFields || [];
	}
};

export const setError = (defaultErrorMessage: string) => {
	if (store.state.errorMessage) {
		store.state.errorMessage =
			'<div>' + defaultErrorMessage + '</div><br/><div>' + store.state.errorMessage + '</div>';
	} else {
		store.state.errorMessage = defaultErrorMessage;
	}
};
